import React, { useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { slideVariants } from "./MobileStep";

const formatTime = (s) =>
  `${Math.floor(s / 60)
    .toString()
    .padStart(2, "0")}:${(s % 60).toString().padStart(2, "0")}`;

/**
 * OtpStep — Step 2: enter the 6-digit OTP
 * Props: mobile, otpInput, otpError, resendCountdown, expiryCountdown,
 *        onOtpChange, onVerify, onResend, onBack
 */
const OtpStep = ({
  mobile,
  otpInput,
  otpError,
  resendCountdown,
  expiryCountdown,
  onOtpChange,
  onVerify,
  onResend,
  onBack,
  verifyingOTP,
}) => {
  const otpRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => otpRef.current?.focus(), 120);
    return () => clearTimeout(timer);
  }, []);

  const handlePaste = (event) => {
    event.preventDefault();
    onOtpChange(event.clipboardData.getData("text"));
  };

  return (
    <motion.div
      key="otp"
      variants={slideVariants}
      initial="enter"
      animate="center"
      exit="exit"
      className="flex flex-col items-center"
    >
      {/* Icon + heading */}
      <div className="flex flex-col items-center mb-5">
        <div
          className="w-14 h-14 rounded-2xl flex items-center justify-center mb-3 shadow-lg shadow-emerald-600/20 border border-emerald-400/30"
          style={{ background: "linear-gradient(135deg, #059669, #047857)" }}
        >
          <motion.svg
            className="w-7 h-7 text-white"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth="2"
            animate={{ y: [0, -4, 0] }}
            transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
            />
          </motion.svg>
        </div>
        <h2 className="text-2xl font-semibold text-gray-700 mb-1 tracking-tight">
          Verify OTP
        </h2>
        <p className="text-xs text-slate-500 font-medium text-center pt-2">
          6-digit code sent to
        </p>
        <p className="text-xs font-black text-emerald-700 mt-1">+91 {mobile}</p>
      </div>

      {/* One real input lets browsers autofill the entire code. */}
      <div className="relative mb-3 rounded-xl focus-within:ring-2 focus-within:ring-emerald-500/40">
        <input
          ref={otpRef}
          id="otp-code"
          name="otp"
          aria-label="6-digit verification code"
          aria-invalid={Boolean(otpError)}
          aria-describedby={otpError ? "otp-error" : undefined}
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          value={otpInput.join("")}
          onChange={(event) => onOtpChange(event.target.value)}
          onPaste={handlePaste}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !verifyingOTP) onVerify();
          }}
          className="absolute inset-0 z-10 w-full h-full opacity-0 cursor-text"
        />
        <div aria-hidden="true" className="flex justify-center gap-2 pointer-events-none">
          {otpInput.map((digit, idx) => (
            <div
              key={idx}
              className={`w-10 sm:w-11 flex items-center justify-center border-2 rounded-xl text-xl font-black text-emerald-700 shadow-sm ${
                digit
                  ? "border-emerald-600 bg-emerald-50/40"
                  : "border-[#22c55e]/30 bg-slate-50"
              }`}
              style={{ height: "48px" }}
            >
              {digit}
            </div>
          ))}
        </div>
      </div>

      {/* Hidden expiry timer — keeps logic alive without showing in UI */}
      <span style={{ display: "none" }}>{formatTime(expiryCountdown)}</span>

      {/* OTP error */}
      <AnimatePresence>
        {otpError && (
          <motion.span
            id="otp-error"
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="text-[11px] text-rose-500 font-bold mb-3 text-center"
          >
            {otpError}
          </motion.span>
        )}
      </AnimatePresence>

      {/* Resend timer / button */}
      <div className="text-center text-xs mb-4 w-full">
        {resendCountdown > 0 ? (
          <p className="text-slate-400 font-semibold">
            Resend in{" "}
            <span className="text-emerald-700 font-black">
              {formatTime(resendCountdown)}
            </span>
          </p>
        ) : (
          <button
            id="login-resend-btn"
            onClick={onResend}
            className="text-emerald-700 font-semibold hover:underline text-xs"
          >
            Resend Verification Code
          </button>
        )}
      </div>

      {/* Verify button */}
      <motion.button
        id="login-verify-btn"
        onClick={onVerify}
        disabled={verifyingOTP}
        whileHover={verifyingOTP ? {} : { scale: 1.01 }}
        whileTap={verifyingOTP ? {} : { scale: 0.97 }}
        className="w-full py-3.5 text-white font-semibold rounded-2xl text-base shadow-lg flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed transition-all duration-200"
        style={{
          background: "linear-gradient(90deg, #00713b 0%, #04b26b 100%)",
        }}
      >
        {verifyingOTP ? (
          <>
            <svg
              className="animate-spin w-5 h-5 text-white"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v8z"
              />
            </svg>
            Verifying…
          </>
        ) : (
          "Verify"
        )}
      </motion.button>

      {/* Back link */}
      <div className="flex items-center gap-3 mt-3">
        <div className="w-32 flex-1 border border-slate-200"></div>
        <span className="text-xs text-slate-400">or</span>
        <div className="flex-1 border border-t border-slate-200"></div>
      </div>

      <button
        onClick={onBack}
        className="mt-1 text-xs text-emerald-700 font-semibold hover:text-slate-700 transition-colors flex items-center justify-center gap-2 w-full"
      >
        <span>
          <i className="las la-sync text-xl text-emerald-700"></i>
        </span>
        <span>Change number</span>
      </button>
    </motion.div>
  );
};

export default OtpStep;
